import { useCallback } from "react";
import ExcelJS from "exceljs";
import { showToast } from "@int-traderjoes/ui-components-library";
import { AllergenData } from "@/hooks/useAllergens";
import { getSpecificationsForVendorGroup } from "@/utils/productInfoDetailsSelectors";
import { handleApiError } from "@/hooks/useApiErrorHandler";

interface UseAllergensExportParams {
  visibleAllergensData: AllergenData[];
  allergenDeclarationData: any;
  computedContainsMayContain: { contains: string; mayContain: string };
  normalizedSpecType: string;
  tableHeaderText: any;
  languageText: any;
  selectedActiveProject: any;
  selectedActiveProjectSelectedVendor: string | number | null;
  activeVariantIndex?: number;
  onExportComplete?: () => void;
}

export const useAllergensExport = ({
  visibleAllergensData,
  allergenDeclarationData,
  computedContainsMayContain,
  normalizedSpecType,
  tableHeaderText,
  languageText,
  selectedActiveProject,
  selectedActiveProjectSelectedVendor,
  activeVariantIndex,
  onExportComplete,
}: UseAllergensExportParams) => {
  const handleExportAsExcel = useCallback(async () => {
    if (onExportComplete) {
      onExportComplete();
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Allergens & Sensitivities");

      // Title
      let row = sheet.addRow(["=== ALLERGENS AND SENSITIVITIES ==="]);
      row.getCell(1).font = { bold: true };
      sheet.addRow([]);

      // Detailed allergen table
      const headerAllergen = tableHeaderText?.ALLERGEN_INDICATE_ALL_THAT_APPLY_TO_INGREDIENTS_AND_SUBINGREDIENTS || 'Allergen';
      const headerRisk = tableHeaderText?.CHOOSE_ONE_STATEMENT_TO_BEST_DESCRIBE_THE_HIGHEST_RISK_OF_PRESENCE_OF_ALLERGEN_IN_THE_PRODUCT || 'Risk Statement';
      const headerFormal = tableHeaderText?.FORMAL_CONTROL_PROGRAM || 'Formal Control Program';
      const headerDeclared = tableHeaderText?.DECLARED || 'Declared';
      const headerLabelDec = tableHeaderText?.LABEL_DECLARATION || 'Label Declaration';
      const headerSpecify = tableHeaderText?.SPECIFY || 'Specify';
      const headerSource = tableHeaderText?.SOURCE || 'Source';

      if (Array.isArray(visibleAllergensData) && visibleAllergensData.length > 0) {
        const headerRow = sheet.addRow([headerAllergen, headerRisk, headerFormal, headerDeclared, headerLabelDec, headerSpecify, headerSource]);
        headerRow.font = { bold: true };

        const stripHtml = (s: any) => String(s ?? '').replace(/<[^>]*>/g, '');
        visibleAllergensData.forEach((a: any) => {
          sheet.addRow([
            stripHtml(a?.allergen || a?.label || ''),
            a?.riskStatement || '',
            a?.formalControlProgram || '',
            a?.declared || '',
            a?.labelDeclaration || '',
            a?.specify || '',
            a?.source || ''
          ]);
        });
      }
      sheet.addRow([]);

      // Declarations
      row = sheet.addRow(["=== DECLARATIONS ==="]);
      row.getCell(1).font = { bold: true };
      sheet.addRow([]);
      const fvRow = sheet.addRow(["Field Name", "Value"]);
      fvRow.font = { bold: true };

      // Contains / May Contain
      sheet.addRow(["Contains", computedContainsMayContain?.contains || " "]);
      sheet.addRow(["May Contain", computedContainsMayContain?.mayContain || " "]);
      
      // Other Allergen Declarations
      const otherDecls = Array.isArray((allergenDeclarationData as any)?.otherAllergenDeclarations)
        ? (allergenDeclarationData as any).otherAllergenDeclarations
        : [];
      if (otherDecls.length > 0) {
        otherDecls.forEach((decl: any) => {
          const template = Array.isArray(decl?.option?.template) ? decl.option.template : [];
          const inputs = Array.isArray(decl?.inputs) ? decl.inputs : [];
          const nonEmptyInputs = inputs.filter((v: any) => v != null && String(v).trim() !== '');
          let built = '';
          let idx = 0;
          template.forEach((part: any) => {
            if (part?.type === 'text') built += String(part?.value ?? '');
            else if (part?.type === 'input') { built += String(nonEmptyInputs[idx] ?? ''); idx += 1; }
          });
          sheet.addRow(["Other Allergen Declarations", built || " "]);
        });
      } else {
        // Always show Other Allergen Declarations field even if empty
        sheet.addRow(["Other Allergen Declarations", " "]);
      }

      // Auto-size columns (basic)
      sheet.columns.forEach((col) => {
        let maxLength = 10;
        col.eachCell?.({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
          const len = String(cell.value ?? '').length;
          if (len > maxLength) maxLength = len;
        });
        col.width = Math.min(Math.max(maxLength + 2, 12), 60);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
       // Get vendor index from selectedActiveProjectSelectedVendor
       let vendorIndex = 0;
       if (selectedActiveProjectSelectedVendor && selectedActiveProject?.vendorDetails) {
         const foundIndex = selectedActiveProject.vendorDetails.findIndex(
           (vendor: any) => String(vendor?.id) === String(selectedActiveProjectSelectedVendor) || 
                           String(vendor?.vendorId) === String(selectedActiveProjectSelectedVendor)
         );
         if (foundIndex >= 0) {
           vendorIndex = foundIndex;
         }
       }
       
        // Get productName from merged assessment specs (vendorList[0])
        const variantIndex = activeVariantIndex ?? 0;
        const productSpecifications = getSpecificationsForVendorGroup(
          selectedActiveProject,
          vendorIndex,
          0
        ) as { productName?: string }[];
        const apiProductName = productSpecifications[variantIndex]?.productName;
        const productName = apiProductName !== null && apiProductName !== undefined ? apiProductName : '';
        
        // Get title from selectedActiveProject (e.g., "Mango bar")
        const projectTitle = selectedActiveProject?.title || '';
        
        // Check if it's a multipack product by checking productSpecification array length
        const isMultipack = Array.isArray(productSpecifications) && productSpecifications.length > 1;
        
        // Generate filename: include productName only for multipack products
        const fileName = isMultipack && productName
          ? `Allergens-and-Sensitivities-${projectTitle}-${productName}.xlsx`
          : `Allergens-and-Sensitivities-${projectTitle}.xlsx`;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        message: languageText?.MESSAGES?.TOAST_MESSAGES?.ACTIVE_PROJECTS?.EXPORT_ALLERGENS_DATA_AS_EXCEL_SUCCESS || 'Allergens and Sensitivities exported successfully'
      });
    } catch (error) {
      console.error('Error exporting allergens and sensitivities as excel:', error);
      handleApiError(error, languageText?.MESSAGES?.TOAST_MESSAGES?.ACTIVE_PROJECTS?.EXPORT_ALLERGENS_DATA_AS_EXCEL_ERROR 
          || 'Failed to export Allergens and Sensitivities as excel');
    }
  }, [
    visibleAllergensData,
    allergenDeclarationData,
    computedContainsMayContain,
    normalizedSpecType,
    tableHeaderText,
    languageText,
    selectedActiveProject,
    selectedActiveProjectSelectedVendor,
    activeVariantIndex,
    onExportComplete,
  ]);

  return {
    handleExportAsExcel,
  };
};

